import classNames from "classnames";
import { useContext, useEffect, useState } from "react";
import { common_utils } from "../../../../../../common/common_utils";
import { Constants } from "../../../../../../common/constants";
import { EventCreateMentions } from "../../../../../../common/sesh_types";
import { ServerReminderDescriptor } from "../../../../../../src/entity/ServerReminder";
import { DashboardUtils, RevertDescriptor } from "../../../dashboard_utils";
import { ModifySettingsButton } from "../base_components/modify_settings_button";
import { Disabler } from "../disabler";
import { useChannelName } from "../hooks/use_channel_name";
import { ErrorTooltip } from "../tooltips/error_tooltip";
import { WithTooltip } from "../tooltips/with_tooltip";
import {
	ActiveMentionsModal,
	RoleMention,
	UserMention,
} from "./initial_mentions_blurb";
import { ServerRemindersModal } from "./server_reminders_modal";
import { cloneDeep } from "lodash";
import { EventCreationContext } from "../../../event_creation";
import { SimpleSelectableBoxItem } from "../simple_selectable_box_item";
import { MentionsList } from "./mentions_list";

export type ReminderTimeUnit = keyof typeof ReminderTimeUnitDescriptors;

export const ReminderTimeUnitDescriptors = {
	weeks: 1000 * 60 * 60 * 24 * 7,
	days: 1000 * 60 * 60 * 24,
	hours: 1000 * 60 * 60,
	minutes: 1000 * 60,
} as const;

type Mentions = {
	user_ids: string[];
	role_ids: string[];
};

export function CreateNewDefaultReminder(
	is_first: boolean,
	mentions?: Mentions
): ServerReminderDescriptor {
	return {
		...cloneDeep(Constants.DEFAULT_EVENT_SERVER_REMINDERS[0]),
		fire_before_ms: is_first ? 0 : 1000 * 60 * 10,
		role_mention_ids: mentions?.role_ids ?? [],
		user_mention_ids: mentions?.user_ids ?? [],
	};
}

type LastReminderEdited = {
	index: number | "create_mentions" | "all";
	created_at_ms: number;
};

function getCreatedAt(
	last_reminder_edited: LastReminderEdited,
	index: LastReminderEdited["index"]
) {
	if (last_reminder_edited.index === "all") {
		return last_reminder_edited.created_at_ms;
	}

	return last_reminder_edited.index === index
		? last_reminder_edited.created_at_ms
		: undefined;
}

type Props = {
	reminders: ServerReminderDescriptor[];
	setReminders: (reminders: ServerReminderDescriptor[]) => void;
	initial_mentions: EventCreateMentions;
	setInitialMentions: (mentions: EventCreateMentions) => void;
	has_unsaved_changes?: boolean;
	is_channel_override_active?: boolean;
	revert_descriptor?: RevertDescriptor<any>[];
	event_channel_id?: string;
	event_start_time?: Date;
	is_disabled: boolean;
};

type FormState = "Root" | "CustomizingNotifications";

type ActiveModalState = "StartMentions" | "CreateMentions" | undefined;

export function ServerRemindersForm(props: Props) {
	const [state, setState] = useState("Root" as FormState);

	const [mentions_to_mirror, setMentionsToMirror] = useState(
		undefined as Mentions | undefined
	);

	const [modal_state, setModalState] = useState(
		undefined as ActiveModalState
	);

	function updateMentionsOnAllReminders(mentions: Mentions) {
		props.setReminders(
			props.reminders.map((val) => ({
				...val,
				role_mention_ids: mentions.role_ids,
				user_mention_ids: mentions.user_ids,
			}))
		);
	}

	const [reminder_edited_info, setLastReminderEditedInfo] =
		useState<LastReminderEdited>({
			index: -1,
			created_at_ms: 0,
		});

	const event_start_reminders = props.reminders.filter(
		(val) => val.fire_before_ms === 0
	);

	// Event start reminder to modify priority:
	// 1. Reminder in event's channel
	// 2. Reminder in event's channel but not explicitly set to event's channel
	// 3. First reminder with fire time of 0
	const event_start_reminder =
		event_start_reminders.find((val) => !val.desired_channel_id) ??
		event_start_reminders.find(
			(val) => val.desired_channel_id === props.event_channel_id
		) ??
		event_start_reminders[0];

	return (
		<>
			<div
				className={classNames("field", {
					"overridden-field": props.is_channel_override_active,
					"active blurb": !props.is_channel_override_active,
				})}
			>
				{DashboardUtils.CreateHeaderAndInfoButton(
					"Notifications & Mentions",
					"Customizable notifications that will be sent to specific channels in the server",
					false,
					false,
					props.has_unsaved_changes,
					undefined,
					props.is_disabled,
					false,
					props.revert_descriptor
				)}
				{state === "Root" && (
					<ServerRemindersStateButtons
						create_mentions={{
							role_ids:
								props.initial_mentions.create_role_mentions,
							user_ids:
								props.initial_mentions.create_user_mentions,
						}}
						start_reminder={event_start_reminder}
						onCustomizeNotificationsClicked={() =>
							setState("CustomizingNotifications")
						}
						onCreateMentionsClicked={() => {
							setModalState("CreateMentions");
						}}
						onStartMentionsClicked={() => {
							setModalState("StartMentions");
						}}
					/>
				)}
				{state === "CustomizingNotifications" && (
					<CustomizeNotificationsForm
						{...props}
						onBackClick={() => setState("Root")}
						mentions_to_mirror={mentions_to_mirror}
						reminder_edited_info={reminder_edited_info}
						setLastReminderEditedInfo={setLastReminderEditedInfo}
						is_mentions_modal_active={
							modal_state === "CreateMentions"
						}
						setMentionsModalActive={(val) =>
							setModalState(val ? "CreateMentions" : undefined)
						}
					/>
				)}
			</div>
			<MentionsModal
				header="Mentions on Event Create"
				initial_role_mentions={
					props.initial_mentions.create_role_mentions
				}
				initial_user_mentions={
					props.initial_mentions.create_user_mentions
				}
				is_active={modal_state === "CreateMentions"}
				onClose={() => setModalState(undefined)}
				onSubmit={(mentions) => {
					props.setInitialMentions({
						create_role_mentions: mentions.role_ids,
						create_user_mentions: mentions.user_ids,
					});
				}}
				setMentionsToMirror={(val) => {
					setMentionsToMirror(val);

					if (val) {
						updateMentionsOnAllReminders(val);
						setLastReminderEditedInfo({
							created_at_ms: Date.now(),
							index: "all",
						});
					}
				}}
				is_disabled={props.is_disabled}
				is_mirroring={mentions_to_mirror !== undefined}
			/>
			<MentionsModal
				header="Mentions on Event Start"
				initial_role_mentions={event_start_reminder?.role_mention_ids}
				initial_user_mentions={event_start_reminder?.user_mention_ids}
				is_active={modal_state === "StartMentions"}
				onClose={() => setModalState(undefined)}
				onSubmit={(mentions) => {
					const reminders_copy = [...props.reminders];
					reminders_copy[
						props.reminders.indexOf(event_start_reminder)
					] = {
						...event_start_reminder,
						role_mention_ids: mentions.role_ids,
						user_mention_ids: mentions.user_ids,
					};
					props.setReminders(reminders_copy);
				}}
				setMentionsToMirror={(val) => {
					setMentionsToMirror(val);

					if (val) {
						updateMentionsOnAllReminders(val);
						props.setInitialMentions({
							create_role_mentions: val.role_ids,
							create_user_mentions: val.user_ids,
						});
						setLastReminderEditedInfo({
							created_at_ms: Date.now(),
							index: "all",
						});
					}
				}}
				is_disabled={props.is_disabled}
				is_mirroring={mentions_to_mirror !== undefined}
			/>
		</>
	);
}

function MentionsModal(props: {
	is_active: boolean;
	initial_role_mentions: string[];
	initial_user_mentions: string[];
	header: string;
	onSubmit: (val: Mentions) => void;
	onClose: () => void;
	is_disabled?: boolean;
	setMentionsToMirror: (val: Mentions) => void;
	is_mirroring?: boolean;
}) {
	return (
		<>
			{props.is_active && (
				<ActiveMentionsModal
					header={props.header}
					role_mention_ids={props.initial_role_mentions}
					user_mention_ids={props.initial_user_mentions}
					onClose={props.onClose}
					onSubmit={(user_ids, role_ids, is_mirroring) => {
						props.onClose();

						if (props.is_disabled) {
							return;
						}

						const mentions: Mentions = {
							role_ids,
							user_ids,
						};

						props.onSubmit(mentions);
						// props.setInitialMentions(mentions);

						props.setMentionsToMirror(
							is_mirroring ? mentions : undefined
						);
					}}
					is_mirroring_mentions={props.is_mirroring}
				/>
			)}
		</>
	);
}

function ServerRemindersStateButtons(props: {
	create_mentions: Mentions;
	start_reminder: ServerReminderDescriptor | undefined;
	onCustomizeNotificationsClicked: () => void;
	onStartMentionsClicked: () => void;
	onCreateMentionsClicked: () => void;
}) {
	return (
		<div className="flex w-full flex-col gap-1">
			<SimpleSelectableBoxItem
				header={`Mentions on Event Create`}
				description={
					<div className="flex w-full gap-2 overflow-hidden">
						<MentionsList
							role_mention_ids={props.create_mentions.role_ids}
							user_mention_ids={props.create_mentions.user_ids}
						/>
					</div>
				}
				onClick={props.onCreateMentionsClicked}
				bgColor="dark"
			></SimpleSelectableBoxItem>
			<SimpleSelectableBoxItem
				header={"Mentions on Event Start"}
				disabled={!props.start_reminder}
				description={
					!props.start_reminder ? (
						<div>No event start notification set</div>
					) : (
						<div className="flex w-full gap-2 overflow-hidden">
							<MentionsList
								role_mention_ids={
									props.start_reminder?.role_mention_ids ?? []
								}
								user_mention_ids={
									props.start_reminder?.user_mention_ids ?? []
								}
							/>
						</div>
					)
				}
				onClick={props.onStartMentionsClicked}
				bgColor="dark"
			></SimpleSelectableBoxItem>
			<SimpleSelectableBoxItem
				header={"Customize Notifications"}
				description={""}
				onClick={props.onCustomizeNotificationsClicked}
				bgColor="dark"
			></SimpleSelectableBoxItem>
		</div>
	);
}

function CustomizeNotificationsForm(
	props: Props & {
		onBackClick: () => void;
		mentions_to_mirror:
			| { user_ids: string[]; role_ids: string[] }
			| undefined;
		reminder_edited_info: LastReminderEdited;
		setLastReminderEditedInfo: (info: LastReminderEdited) => void;
		is_mentions_modal_active: boolean;
		setMentionsModalActive: (val: boolean) => void;
	}
) {
	const {
		mentions_to_mirror,
		reminder_edited_info,
		is_mentions_modal_active,
		setLastReminderEditedInfo,
		setMentionsModalActive,
	} = props;

	const [edited_reminder, setEditedReminder] = useState<
		ServerReminderDescriptor | undefined
	>(undefined);

	const [edited_reminder_index, setEditedReminderIndex] = useState(undefined);

	const sorted_index_to_true_index_map: Record<number, number> = {};
	const sorted_reminders = [...props.reminders].sort(
		(a, b) => b.fire_before_ms - a.fire_before_ms
	);
	sorted_reminders.forEach((val, index) => {
		sorted_index_to_true_index_map[index] = props.reminders.findIndex(
			(v) => v === val
		);
	});

	const { reminders, setReminders } = props;

	const max_server_reminders_reached =
		props.reminders.length >= Constants.MAX_SERVER_REMINDERS_COUNT;

	let error_messages: (JSX.Element | undefined)[] = Array(
		props.reminders.length
	).fill(undefined);

	const reminders_copy = [...props.reminders];
	for (let i = reminders_copy.length - 1; i >= 0; i--) {
		let { is_valid, error_message } =
			common_utils.ValidateReminderWithIndex(
				reminders_copy,
				i,
				props.event_channel_id
			);

		if (is_valid && props.event_start_time) {
			if (
				props.event_start_time.getTime() -
					reminders_copy[i].fire_before_ms <
				Date.now() - 1000 * 60
			) {
				is_valid = false;
				error_message = "Can not create a reminder in the past";
			}
		}

		if (!is_valid) {
			error_messages[i] = (
				<div>
					<div>{error_message}</div>
					<br />
					<div>This reminder will not be sent</div>
				</div>
			);
			reminders_copy.splice(i, 1);
		}
	}

	return (
		<>
			<Disabler disabled={props.is_disabled}>
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-1">
							<div className="flex flex-col gap-1">
								<EventStartItem
									event_channel_id={props.event_channel_id}
									created_at_ms={getCreatedAt(
										reminder_edited_info,
										"create_mentions"
									)}
									mentions={props.initial_mentions}
									onEditClicked={() => {
										setMentionsModalActive(true);
									}}
								/>
								{sorted_reminders.map((reminder, index) => {
									const true_index =
										sorted_index_to_true_index_map[index];

									return (
										<ReminderItem
											key={true_index}
											event_channel_id={
												props.event_channel_id
											}
											reminder={reminder}
											error_message={
												error_messages[true_index]
											}
											created_at_ms={getCreatedAt(
												reminder_edited_info,
												true_index
											)}
											onDeleteClicked={() => {
												if (props.is_disabled) return;
												setReminders(
													reminders.filter(
														(val, filter_index) =>
															filter_index !==
															true_index
													)
												);
											}}
											onEditClicked={() => {
												if (props.is_disabled) return;
												setEditedReminder(
													cloneDeep(reminder)
												);
												setEditedReminderIndex(
													true_index
												);
											}}
											onDuplicateClicked={
												max_server_reminders_reached
													? undefined
													: () => {
															if (
																props.is_disabled
															)
																return;
															setEditedReminder(
																cloneDeep(
																	reminder
																)
															);
													  }
											}
										/>
									);
								})}
								<div
									className={`flex flex-col items-center gap-4 rounded-sm bg-base-light p-3 shadow-sm`}
								>
									<ModifySettingsButton
										disabled={
											max_server_reminders_reached ||
											props.is_disabled
										}
										hide_arrow
										onClick={() => {
											setEditedReminder({
												...CreateNewDefaultReminder(
													props.reminders.length ===
														0,
													mentions_to_mirror
														? props.mentions_to_mirror
														: undefined
												),
												fire_before_ms: undefined,
											});
										}}
									>
										{max_server_reminders_reached ? (
											<span>Maximum Notifications</span>
										) : (
											<span>Add Notification</span>
										)}
									</ModifySettingsButton>
								</div>
							</div>
						</div>
					</div>
					<ModifySettingsButton
						hide_arrow
						onClick={props.onBackClick}
					>
						<div className="flex items-center gap-2">
							<i
								key="button-icon"
								className="fas fa-arrow-left"
							></i>
							<div>Back</div>
						</div>
					</ModifySettingsButton>
				</div>
			</Disabler>
			{!is_mentions_modal_active && edited_reminder && (
				<ServerRemindersModal
					value={edited_reminder}
					other_reminders={reminders.filter(
						(val, index) => index !== edited_reminder_index
					)}
					event_channel_id={props.event_channel_id}
					is_edit={edited_reminder_index !== undefined}
					onClose={() => {
						setEditedReminder(undefined);
						setEditedReminderIndex(undefined);
					}}
					addReminder={(reminder) => {
						const reminders_clone = [...reminders];
						if (edited_reminder_index !== undefined) {
							reminders_clone[edited_reminder_index] = {
								...reminder,
								has_fired: false,
							};
						} else {
							reminders_clone.push(reminder);
						}

						setLastReminderEditedInfo({
							index:
								edited_reminder_index ??
								reminders_clone.length - 1,
							created_at_ms: Date.now(),
						});
						setReminders(reminders_clone);
						setEditedReminder(undefined);
						setEditedReminderIndex(undefined);
					}}
				/>
			)}
		</>
	);
}

function EventStartItem(props: {
	event_channel_id: string | undefined;
	mentions: EventCreateMentions;
	error_message?: JSX.Element | string;
	onEditClicked: () => void;
	created_at_ms?: number;
}) {
	const is_newly_created = Date.now() - (props.created_at_ms ?? 0) < 500;

	const [should_glow, setShouldGlow] = useState(is_newly_created);

	const event_creation_context = useContext(EventCreationContext);

	if (is_newly_created && !should_glow) {
		setShouldGlow(true);
	}

	useEffect(() => {
		if (should_glow) {
			const timeout = setTimeout(() => {
				setShouldGlow(false);
			}, 1000);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [should_glow]);

	const channel_name = props.event_channel_id
		? useChannelName(props.event_channel_id, false)
		: "event's channel";

	return (
		<div
			className={classNames(
				"relative box-border flex w-full min-w-0 gap-3 rounded-sm border-2 bg-base-light p-3 shadow-sm ease-out",
				{
					"border-sesh-primary": should_glow,
					"border-transparent transition duration-500": !should_glow,
				}
			)}
		>
			{event_creation_context?.props.is_edit &&
				!event_creation_context.state.rrule && (
					<WithTooltip style="Info" tooltip={"Message already sent"}>
						<i className="fas fa-check-circle success-text"></i>
					</WithTooltip>
				)}
			<div className="flex w-[20%] min-w-0">
				<WithTooltip style={"Info"} tooltip="Fire time">
					<div className="flex w-full items-center gap-1">
						<i className="fas fa-bell opacity-50"></i>
						<div className="min-w-0 whitespace-nowrap">
							<span className="font-bold">Event create</span>
						</div>
					</div>
				</WithTooltip>
			</div>
			<div className="hidden w-[20%] min-w-0 sm:flex">
				<WithTooltip style="Info" tooltip={channel_name}>
					<div className="flex items-center gap-1">
						<i className="fas fa-hashtag opacity-50"></i>
						<div className="min-w-0 overflow-hidden overflow-ellipsis whitespace-nowrap">
							{channel_name}
						</div>
					</div>
				</WithTooltip>
			</div>
			<div className="hidden w-[30%] min-w-0 sm:flex">
				<WithTooltip
					style="Info"
					tooltip={"The signup for the message for this event"}
				>
					<div className="flex items-center gap-1">
						<div>
							<i className="fas fa-comment-alt opacity-50"></i>
						</div>
						<div className="mr-4 min-w-0 overflow-hidden overflow-ellipsis whitespace-nowrap italic">
							Signup message
						</div>
					</div>
				</WithTooltip>
			</div>
			<div className="hidden  w-1/12 min-w-0 items-center gap-1 sm:flex">
				<WithTooltip
					style="Info"
					tooltip={
						<div className="flex flex-wrap gap-2">
							{props.mentions.create_role_mentions.length === 0 &&
								props.mentions.create_user_mentions.length ===
									0 && <>None</>}
							{props.mentions.create_role_mentions.map((val) => (
								<RoleMention key={val} id={val} />
							))}
							{props.mentions.create_user_mentions.map((val) => (
								<UserMention key={val} id={val} />
							))}
						</div>
					}
				>
					<div className="flex items-center gap-1">
						<div>
							<i className="fas fa-at opacity-50"></i>
						</div>
						<div className="min-w-0 overflow-hidden overflow-ellipsis whitespace-nowrap">
							{props.mentions.create_role_mentions.length +
								props.mentions.create_user_mentions.length}
						</div>
					</div>
				</WithTooltip>
			</div>
			<div className="ml-auto">
				<ActionButtons onEditClicked={props.onEditClicked} />
			</div>
		</div>
	);
}

function ReminderItem(props: {
	reminder: ServerReminderDescriptor;
	event_channel_id: string | undefined;
	error_message?: JSX.Element | string;
	onDeleteClicked: () => void;
	onEditClicked: () => void;
	onDuplicateClicked: (() => void) | undefined;
	created_at_ms?: number;
}) {
	const { count, unit } = getReminderDiscretizedTime(props.reminder);

	const is_newly_created = Date.now() - (props.created_at_ms ?? 0) < 500;

	const [should_glow, setShouldGlow] = useState(is_newly_created);

	const event_creation_context = useContext(EventCreationContext);

	if (is_newly_created && !should_glow) {
		setShouldGlow(true);
	}

	const should_send_to_thread =
		event_creation_context?.state.thread_settings
			?.should_send_start_message_to_thread;

	useEffect(() => {
		if (should_glow) {
			const timeout = setTimeout(() => {
				setShouldGlow(false);
			}, 1000);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [should_glow]);

	let channel_name: string;
	if (props.reminder.desired_channel_id) {
		channel_name = useChannelName(props.reminder.desired_channel_id, false);
	} else if (props.event_channel_id) {
		channel_name = should_send_to_thread
			? "event thread"
			: useChannelName(props.event_channel_id, false);
	} else {
		channel_name = "event's channel";
	}

	return (
		<div
			className={classNames(
				"relative box-border flex w-full min-w-0 gap-3 rounded-sm border-2 bg-base-light p-3 shadow-sm ease-out",
				{
					"border-sesh-primary": should_glow,
					"border-transparent transition duration-500": !should_glow,
				}
			)}
		>
			{props.error_message && !props.reminder.has_fired && (
				<ErrorTooltip info={props.error_message} />
			)}
			{props.error_message && !props.reminder.has_fired && (
				<div className="pointer-events-none absolute left-0 top-0 flex h-full w-full bg-warning px-4 py-3 opacity-20"></div>
			)}
			{props.reminder.has_fired && (
				<WithTooltip style="Info" tooltip={"Reminder already sent"}>
					<i className="fas fa-check-circle success-text"></i>
				</WithTooltip>
			)}
			<div className="flex w-[20%] min-w-0">
				<WithTooltip style={"Info"} tooltip="Fire time">
					<div className="flex w-full items-center gap-1">
						<i className="fas fa-bell opacity-50"></i>
						<div className="min-w-0 whitespace-nowrap">
							{count === 0 ? (
								<span className="font-bold">Event start</span>
							) : (
								`${count} ${
									count === 1
										? unit
												.toLowerCase()
												.substring(0, unit.length - 1)
										: unit
								}`
							)}
						</div>
					</div>
				</WithTooltip>
			</div>
			<div className="hidden w-[20%] min-w-0 sm:flex">
				<WithTooltip style="Info" tooltip={channel_name}>
					<div className="flex items-center gap-1">
						<i className="fas fa-hashtag opacity-50"></i>
						<div className="min-w-0 overflow-hidden overflow-ellipsis whitespace-nowrap">
							{channel_name}
						</div>
					</div>
				</WithTooltip>
			</div>
			<div className="hidden w-[30%] min-w-0 sm:flex">
				<WithTooltip
					style="Info"
					tooltip={
						<div>
							<div>
								Title: {props.reminder.message_title ?? "none"}
							</div>
							<div>
								Description:{" "}
								{props.reminder.message_description ?? "none"}
							</div>
						</div>
					}
				>
					<div className="flex items-center gap-1">
						<div>
							<i className="fas fa-comment-alt opacity-50"></i>
						</div>
						<div className="min-w-0 overflow-hidden overflow-ellipsis whitespace-nowrap">
							{props.reminder.message_title
								? props.reminder.message_title
								: props.reminder.message_description}
						</div>
					</div>
				</WithTooltip>
			</div>
			<div className="hidden  w-1/12 min-w-0 items-center gap-1 sm:flex">
				<WithTooltip
					style="Info"
					tooltip={
						<div className="flex flex-wrap gap-2">
							{props.reminder.role_mention_ids.length === 0 &&
								props.reminder.user_mention_ids.length ===
									0 && <>None</>}
							{props.reminder.role_mention_ids.map((val) => (
								<RoleMention key={val} id={val} />
							))}
							{props.reminder.user_mention_ids.map((val) => (
								<UserMention key={val} id={val} />
							))}
						</div>
					}
				>
					<div className="flex items-center gap-1">
						<div>
							<i className="fas fa-at opacity-50"></i>
						</div>
						<div className="min-w-0 overflow-hidden overflow-ellipsis whitespace-nowrap">
							{props.reminder.role_mention_ids.length +
								props.reminder.user_mention_ids.length}
						</div>
					</div>
				</WithTooltip>
			</div>
			<div className="ml-auto">
				<ActionButtons
					onDeleteClicked={props.onDeleteClicked}
					onEditClicked={props.onEditClicked}
					onDuplicateClicked={props.onDuplicateClicked}
				/>
			</div>
		</div>
	);
}

function ActionButtons(props: {
	onDeleteClicked?: () => void;
	onEditClicked?: () => void;
	onDuplicateClicked?: (() => void) | undefined;
}) {
	return (
		<div className="flex gap-4">
			{props.onEditClicked && (
				<WithTooltip
					style="Info"
					tooltip={"Edit notification"}
					disable_click
				>
					<button
						className={classNames("manual-link")}
						onClick={(e) => {
							e.preventDefault();
							props.onEditClicked();
						}}
					>
						<i className="fas fa-pencil-alt" />
					</button>
				</WithTooltip>
			)}
			{props.onDuplicateClicked && (
				<WithTooltip
					style="Info"
					tooltip={"Duplicate notification"}
					disable_click
				>
					<button
						className={classNames("manual-link", {
							"input-disabled": !props.onDuplicateClicked,
						})}
						onClick={(e) => {
							e.preventDefault();
							props.onDuplicateClicked?.();
						}}
					>
						<i className="far fa-clone"></i>
					</button>
				</WithTooltip>
			)}

			{props.onDeleteClicked && (
				<WithTooltip
					style="Info"
					tooltip={"Delete notification"}
					disable_click
				>
					<button
						className={classNames(
							"text-warning hover:text-warning-hover"
						)}
						onClick={(e) => {
							e.preventDefault();
							props.onDeleteClicked();
						}}
					>
						<i className="far fa-trash-alt" />
					</button>
				</WithTooltip>
			)}
		</div>
	);
}

export function reminderDiscretizedTimeToMs(
	val: ReturnType<typeof getReminderDiscretizedTime>
) {
	return val.count * ReminderTimeUnitDescriptors[val.unit];
}

export function getReminderDiscretizedTime(
	reminder: ServerReminderDescriptor
): { count: number; unit: keyof typeof ReminderTimeUnitDescriptors } {
	// Loop through object keys backwards

	if (reminder.fire_before_ms === 0) {
		return {
			count: 0,
			unit: "minutes",
		};
	}

	for (const key in ReminderTimeUnitDescriptors) {
		const current_value_ms =
			ReminderTimeUnitDescriptors[
				key as keyof typeof ReminderTimeUnitDescriptors
			];

		if (reminder.fire_before_ms % current_value_ms === 0) {
			return {
				count: Math.floor(reminder.fire_before_ms / current_value_ms),
				unit: key as keyof typeof ReminderTimeUnitDescriptors,
			};
		}
	}
}
